import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";

export default function Tooltip({ text, content, children, style = {}, tooltipStyle = {} }) {
  const [hovered, setHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const tooltipRef = useRef();
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (hovered) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Set timeout to show tooltip after 300ms
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(true);
      }, 400);
    } else {
      // Clear timeout and hide tooltip immediately when not hovering
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setShowTooltip(false);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hovered]);

  // Handler to update mouse position and clamp within viewport
  const handleMouseMove = (e) => {
    const padding = 8;
    const tooltipWidth = tooltipRef.current ? tooltipRef.current.offsetWidth : 200;
    const tooltipHeight = tooltipRef.current ? tooltipRef.current.offsetHeight : 40;
    // Position top-left corner 10px to the bottom-right of pointer
    let x = e.clientX + 10; // 10px to the right of pointer
    let y = e.clientY + 20; // 10px below pointer
    // Clamp horizontally (right edge)
    if (x + tooltipWidth > window.innerWidth - padding) {
      x = window.innerWidth - tooltipWidth - padding;
    }
    // Clamp horizontally (left edge)
    if (x < padding) {
      x = padding;
    }
    // Clamp vertically (top edge)
    if (y < padding) {
      y = padding;
    }
    // Clamp vertically (bottom edge)
    if (y + tooltipHeight > window.innerHeight - padding) {
      y = window.innerHeight - tooltipHeight - padding;
    }
    setCoords({ x, y });
  };

  const tooltipContent = content || text;

  return (
    <span
      style={{ position: "relative", display: "inline-block", ...style }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onMouseMove={handleMouseMove}
    >
      {children}
      {showTooltip && tooltipContent && ReactDOM.createPortal(
        <span
          ref={tooltipRef}
          style={{
            position: "fixed",
            left: coords.x,
            top: coords.y,
            background: "#fff",
            color: "#111",
            padding: "5px 18px",
            borderRadius: 7,
            fontSize: 12,
            minWidth: 80,
            maxWidth: 260,
            whiteSpace: content ? "normal" : "pre-line",
            boxShadow: "0 4px 16px #0002",
            zIndex: 100001,
            opacity: showTooltip ? 1 : 0,
            pointerEvents: 'none',
            transition: "opacity 0.15s cubic-bezier(.4,2,.6,1)",
            ...tooltipStyle
          }}
        >
          {tooltipContent}
        </span>,
        document.body
      )}
    </span>
  );
} 